import { useEffect, useState } from 'react';
import { getAllInsurerPayouts } from '../../../../api/requests';
import useUserAccount from '../../../../hooks/userAccountHook';
import { Payout } from '../../../../models/payout/Payout';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import useRoute from '../../../../hooks/routesHook';
import GTable from '../../../../components/elements/GTable';
import IconStatus from '../../../../components/elements/IconStatus';
import useModal from '../../../../hooks/modalHook';
import ApprovePayout from '../../ApprovePayoutModal';
import { COMPANY_TYPES, PAYOUT_REVIEW_STATES } from '../../../../resources/enums';
import PayoutReview from './components/PayoutReview';
import MarkAsPaidModal from '../../MarkAsPaidModal';
import PopOnMe from '../../../../components/elements/poponme';
import { EllipsisVerticalIcon } from '@heroicons/react/24/solid';
import Page from '../../../../components/PageWrapper';
import { capitalizeSentence, getFormattedDate, getOrUpdateQueriesFromUrl, onRemoveFilter, showToast, updateQueriesList } from '../../../../utils/miscFunctions';
import { ISelectDropdownItem } from '../../../../components/form-control/SelectDropdown';
import usePartner from '../../../../hooks/partnerHook';
import { Company } from '../../../../models/company/Company';
import TableFilter from '../../../../components/elements/GTable/InGTableComponents/TableFilter';
import TableFilterForm from '../../../../components/elements/GTable/InGTableComponents/TableFilter/TableFilterForm';
import { ITableFilterItem } from '../../../../resources/interfaces';
import { GTableActionColumn, ITablePaginationData } from '../../../../components/elements/GTable/GTable';
import PayoutsOverviewPanel from './components/PayoutsOverviewPanel';
import { getPayoutMetrics } from '../../../../services/payouts'; 
import { LuExternalLink } from 'react-icons/lu';

interface IPayoutsProps {
  onPayoutsUpdated?: (payouts: Payout[]) => void;
  showTitleSection?: boolean;
  setPayoutTab?: () => void;
  settlements?: Payout[];
  selectedBrokerFromSettlements?: any;
}

const PayoutsView = ({ onPayoutsUpdated, showTitleSection = false, setPayoutTab, settlements, selectedBrokerFromSettlements }: IPayoutsProps) => {
  const page = 'premium payout';
  const [showPerPage, setShowPerPage] = useState<number>(10);
  const { getDashboardBase } = useRoute();
  const navigate = useNavigate();
  const { user } = useUserAccount();
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const { ModalComponent, updateModalStates, closeModal } = useModal();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = Number(searchParams.get("page")) || 0;
  const { fetchAllCompaniesByTypes } = usePartner();
  const queryClient = useQueryClient();
  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const [searchString, setSearchString] = useState<string>()
  const queriesList: Record<string, string> = {
    page: String(currentPage)
  }

  const { data: insurersList } = useQuery({
    queryKey: ['companies'],
    queryFn: async () => {
      try {
        const insurers = await fetchAllCompaniesByTypes([COMPANY_TYPES.INSURER]);
        return insurers;
      } catch (error) {
        console.error('Error fetching insurers:', error);
        return [];
      }
    },
    enabled: !!(user.isBroker() || user.isInternalAdmin()),
  });

  const { data: brokersList } = useQuery({
    queryKey: ['brokers'],
    queryFn: () => fetchAllCompaniesByTypes([COMPANY_TYPES.BROKER]),
    enabled: !!(user.isInsurer() || user.isInternalAdmin()),
  });

  const handlePayoutCardClick = (isPaid: boolean | null) => {
    isPaid === null ? fetchPayouts('null') : setTableFilters(prev =>
      prev.map(item =>
        item.name === "payment_status"
          ? { ...item, value: isPaid, value2: isPaid ? 'Paid' : 'Not paid' }
          : item
      )
    );
    if (isPaid === null) {
      setTableFilters(prev =>
        prev.map(item =>
          item.name === "payment_status"
            ? { ...item, value: '', value2: '' }
            : item
        )
      )
    }

  };


  const searchParam = searchString;
  const [tableFilters, setTableFilters] = useState<ITableFilterItem[]>(getDefaultFilters());
  const toDate = tableFilters?.find((filter: ITableFilterItem) => filter?.name === 'create_date')?.value?.to;
  const fromDate = tableFilters?.find((filter: ITableFilterItem) => filter?.name === 'create_date')?.value?.from;
  const isPaid = searchParams.get('payment_status') ? searchParams.get('payment_status') : tableFilters?.find((filter: ITableFilterItem) => filter?.name === 'payment_status')?.value;
  const isApproved = searchParams.get('approved_status') ? searchParams.get('approved_status') : tableFilters?.find((filter: ITableFilterItem) => filter?.name === 'approved_status')?.value;
  const reviewStage = tableFilters?.find((filter: ITableFilterItem) => filter?.name === 'review_stage')?.value;
  const insurer = searchParams.get('insurer') ? searchParams.get('insurer') : tableFilters?.find((filter: ITableFilterItem) => filter?.name === 'insurer')?.value || (user.isInsurer() && user?.company?.id);
  const broker = searchParams.get('broker') ? searchParams.get('broker') : tableFilters?.find((filter: ITableFilterItem) => filter?.name === 'broker')?.value || (user.isBroker() && user?.company?.id);

  const { data, isLoading } = useQuery({
    queryFn: () => fetchPayouts(),
    queryKey: ['fetchPayouts', fromDate, toDate, isPaid, isApproved, reviewStage, currentPage, showPerPage, insurer, broker, searchParam],
    staleTime: 1000 * 10,
    placeholderData: (prev) => prev,
     gcTime: 1000 * 60 * 30 * 5,
    select: (response) => {
  
      return {
        insurerPayouts: response?.data?.content,
        pagination: {
          per_page: response?.data?.size,
          total: response?.data?.totalElements,
          totalPages: response?.data?.totalPages,
          from: response?.data?.pageable?.offset + 1,
          to: response?.data?.totalElements > response?.data?.pageable?.offset + showPerPage
            ? response?.data?.pageable?.offset + showPerPage : response?.data?.totalElements,
        },
      };
    },
  });
  
  const pagination = data?.pagination;
  const insurerPayouts = data?.insurerPayouts;

  const { data: payoutMetrics, isLoading: isLoadingPayoutMetrics } = useQuery({
    queryFn: async () => {
      try {
        const response: any = await getPayoutMetrics(broker, fromDate, toDate, insurer)
        return response?.summary
      } catch {
        return {}
      }
    },
    queryKey: ['getPayoutMetrics', broker, fromDate, toDate, insurer],
  });

  const columns = React.useMemo(
    () => {
      if ((user.isOwner() || user.isSupervisor())) {
        return [
          {
            Header: 'M',
            columns: [
              {
                Header: 'Id',
                accessor: 'id',
                Cell: (row: any) => {
                  return (
                    <div className='flex items-center gap-2'>
                  <Link
                    to={`${getDashboardBase()}/payouts/${row.cell.value}?type=premium`}
                  >
                    <span className="hover:underline text-link-main dark:text-darkBg-textLink dark:hover:text-darkBg-textLink">{row.cell.value ?? '-'}</span>
                  </Link>
                   </div>
                )
                }
                  
              },
               {
                Header: '',
                accessor: 'an',
                Cell: (row: any) => {
                  return (
                    <div className='flex items-center'>
                      <LuExternalLink className='h-5 w-5  cursor-pointer' onClick={()=>window.open(`${getDashboardBase()}/payouts/${row.row.original.id}?type=premium`, '_blank')}/>   
                    </div>
                )
                }
                  
              },
              {
                Header: 'Broker',
                accessor: 'brokerName',
                Cell: (row: any) => {
                  return <span>{row.cell.value ? row.cell.value : '-'}</span>
                }
              },
              {
                Header: 'Insurance Company',
                accessor: 'companyName',
                Cell: (row: any) => {
                  return <span>{row.cell.value ? row.cell.value : '-'}</span>
                }
              },
              {
                Header: 'Reference',
                accessor: 'reference',
                Cell: (row: any) => {
                  return <span>{row.cell.value ? row.cell.value : '-'}</span>;
                }
              },
              {
                Header: 'Currency',
                accessor: 'currency',
                Cell: (row: any) => {
                  return <span>{row.cell.value ? row.cell.value : '-'}</span>;
                }
              },
              {
                Header: 'Amount payable',
                accessor: 'netToPay',
                Cell: (row: any) => {
                  return <span>{row.cell.value ? row.cell.value : '-'}</span>;
                }
              },
              {
                Header: 'Amount paid',
                accessor: 'amountPaid',
                Cell: (row: any) => {
                  return <span>{row.cell.value ? row.cell.value : '-'}</span>;
                }
              },
              {
                Header: 'Date',
                accessor: 'createdOn',
                Cell: (row: any) => {
                  return (
                    <span>{row.cell.value ? getFormattedDate({ date: row.cell.value }) : '-'}</span>
                  );
                }
              },

              {
                Header: 'Reviewed',
                accessor: 'isReviewed',
                Cell: (row: any) => {
                  return <IconStatus status={row.cell.value} cursorPointer toolTipMessage={row.cell.value === true ? 'Payout reviewed' : 'Payout not reviewed'}/>;
                }
              },
              {
                Header: 'Approved',
                accessor: 'isApproved',
                Cell: (row: any) => {
                  return <IconStatus status={row.cell.value} cursorPointer toolTipMessage={row.cell.value === true ? 'Payout approved' : 'Payout not approved'}/>;
                }
              },
              {
                Header: 'Paid',
                accessor: 'isPaid',
                Cell: (row: any) => {
                  return <IconStatus status={row.cell.value} cursorPointer toolTipMessage={row.cell.value === true ? 'Payout paid' : 'Payout not paid'}/>;
                }
              },
              {
                Header: 'Actions',
                id: 'Action',
                Cell: (row: any) => {
                  if(getTableRowActions(row.row.original).length > 0) return (
                    <GTableActionColumn actions={getTableRowActions(row.row.original)}/>
                  );
                }
              }
            ]
          }
        ]
      }

      if (user.isBroker()) {
        return [
          {
            Header: 'M',
            columns: [
               {
                Header: 'Id',
                accessor: 'id',
                Cell: (row: any) => {
                  return (
                  <Link
                    to={`${getDashboardBase()}/payouts/${row.cell.value}?type=premium`}
                  >
                          <span className="hover:underline text-link-main dark:text-darkBg-textLink dark:hover:text-darkBg-textLink">{row.cell.value ?? '-'}</span>
                  </Link>)
                }
                  
              },
              {
                Header: '',
                accessor: 'an',
                Cell: (row: any) => {
                  return (
                    <div className='flex items-center'>
                      <LuExternalLink className='h-5 w-5  cursor-pointer' onClick={()=>window.open(`${getDashboardBase()}/payouts/${row.row.original.id}?type=premium`, '_blank')}/>   
                    </div>
                )
                }
                  
              },
              {
                Header: 'Agency company',
                accessor: 'companyName',
                Cell: (row: any) => {
                  return <span>{row.cell.value ? row.cell.value : '-'}</span>
                }
              },
              {
                Header: 'Reference',
                accessor: 'reference',
                Cell: (row: any) => {
                  return <span>{row.cell.value ? row.cell.value : '-'}</span>;
                }
              },
              {
                Header: 'Currency',
                accessor: 'currency',
                Cell: (row: any) => {
                  return <span>{row.cell.value ? row.cell.value : '-'}</span>;
                }
              },
              {
                Header: 'Amount',
                accessor: 'grossToPay',
                Cell: (row: any) => {
                  return <span>{row.cell.value ? row.cell.value : '-'}</span>;
                }
              },
              {
                Header: 'Date',
                accessor: 'createdOn',
                Cell: (row: any) => {
                  return (
                    <span>{row.cell.value ? getFormattedDate({ date: row.cell.value }) : '-'}</span>
                  );
                }
              },

              {
                Header: 'Reviewed',
                accessor: 'isReviewed',
                Cell: (row: any) => {
                  return <IconStatus status={row.cell.value} classes="" />;
                }
              },
              {
                Header: 'Approved',
                accessor: 'isApproved',
                Cell: (row: any) => {
                  return <IconStatus status={row.cell.value} />;
                }
              },
              {
                Header: 'Paid',
                accessor: 'isPaid',
                Cell: (row: any) => {
                  return <IconStatus status={row.cell.value} />;
                }
              },
              {
                Header:'Actions',
                id: 'Action',
                Cell: (row: any) => {
                  if(getTableRowActions(row.row.original).length > 0) return (
                   <GTableActionColumn actions={getTableRowActions(row.row.original)}/>
                  );
                }
              }
            ]
          }
        ]
      }

      return [
        {
          Header: 'M',
          columns: [
             {
                Header: 'Id',
                accessor: 'id',
                Cell: (row: any) => {
                  return (
                  <Link
                    to={`${getDashboardBase()}/payouts/${row.cell.value}?type=premium`}
                  >
                          <span className="hover:underline text-link-main dark:text-darkBg-textLink dark:hover:text-darkBg-textLink">{row.cell.value ?? '-'}</span>
                  </Link>)
                }
                  
              },
              {
                Header: '',
                accessor: 'an',
                Cell: (row: any) => {
                  return (
                    <div className='flex items-center'>
                      <LuExternalLink className='h-5 w-5  cursor-pointer' onClick={()=>window.open(`${getDashboardBase()}/payouts/${row.row.original.id}?type=premium`, '_blank')}/>   
                    </div>
                )
                }
                  
              },
            {
              Header: 'Reference',
              accessor: 'reference',
              Cell: (row: any) => {
                return <span>{row.cell.value ? row.cell.value : '-'}</span>;
              }
            },
            {
              Header: 'Currency',
              accessor: 'currency',
              Cell: (row: any) => {
                return <span>{row.cell.value ? row.cell.value : '-'}</span>;
              }
            },
            {
              Header: 'Amount',
              accessor: 'grossToPay',
              Cell: (row: any) => {
                return <span>{row.cell.value ? row.cell.value : '-'}</span>;
              }
            },
            {
              Header: 'Date',
              accessor: 'createdOn',
              Cell: (row: any) => {
                return (
                  <span>{row.cell.value ? getFormattedDate({ date: row.cell.value }) : '-'}</span>
                );
              }
            },

            {
              Header: 'Reviewed',
              accessor: 'isReviewed',
              Cell: (row: any) => {
                return <IconStatus status={row.cell.value} classes="" />;
              }
            },
            {
              Header: 'Approved',
              accessor: 'isApproved',
              Cell: (row: any) => {
                return <IconStatus status={row.cell.value} />;
              }
            },
            {
              Header: 'Paid',
              accessor: 'isPaid',
              Cell: (row: any) => {
                return <IconStatus status={row.cell.value} />;
              }
            },
            {
              Header: 'Actions',
              id: 'Action',
              Cell: (row: any) => {
               if(getTableRowActions(row.row.original).length > 0) return (
                  <GTableActionColumn actions={getTableRowActions(row.row.original)} />
                );
              }
            }
          ]
        }
      ]
    },
    [insurer]
  );

  function getDefaultFilters() {
    const filters: ITableFilterItem[] = [
      {
        name: 'create_date',
        value: '',
        type: 'dateRange',
        placeholder: 'Select start date',
        label: 'Date Range'
      },
      {
        name: 'payment_status',
        value: searchParams.get('payment_status') ? String(searchParams.get('payment_status')) : '',
        value2: searchParams.get('payment_status') ? String(searchParams.get('payment_status')) : '',
        list: [{
          name: 'Paid',
          value: 'true'
        },
        {
          name: 'Not paid',
          value: 'false'
        },
        ],
        type: 'select',
        label: 'Payment Status',
        placeholder: 'select status'
      }, {
        name: 'approved_status',
        value: searchParams.get('approved_status') ? String(searchParams.get('approved_status')) : '',
        value2: searchParams.get('approved_status') ? String(searchParams.get('approved_status')) : '',
        list: [{
          name: 'Approve',
          value: 'true'
        },
        {
          name: 'Not approved',
          value: 'false'
        },
        ],
        type: 'select',
        label: 'Approved Status',
        placeholder: 'select status'
      },
    ];

    if (user.isInternalAdmin() || user.isInsurer()) {
      filters.unshift({
        name: 'broker',
        value: searchParams.get('broker') ? String(searchParams.get('broker')) : '',
        value2: searchParams.get('broker') ? String(searchParams.get('broker')) : '',
        list: brokersList?.map(
          (broker: any) =>
          ({
            name: broker.name,
            value: broker.id
          } as ISelectDropdownItem)
        ),
        type: 'select',
        label: 'From broker',
        placeholder: 'select broker'
      })
    }


    if (user.isBroker() || user.isInternalAdmin()) {
      filters.unshift({
        name: 'insurer',
        value: searchParams.get('insurer') ? String(searchParams.get('insurer')) : '',
        value2: searchParams.get('insurer') ? String(searchParams.get('insurer')) : '',
        list: insurersList?.map((insurer: Company) => ({
          name: insurer.name,
          value: insurer.id,
        })),
        showSearch: true,
        type: 'select',
        label: 'To insurer',
        placeholder: 'select insurer'
      })
    }

    return filters;
  }

  const superApprovePayout = (payout: Payout) => {
    if (payout) {
      updateModalStates({
        show: true,
        children: (
          <ApprovePayout
            payout={payout}
            onProceed={(data) => {
              closeModal();
              queryClient.invalidateQueries({
                queryKey: ['getPayouts']
              });
            }}
            onCancel={closeModal}
            title="Approve commission payout"
            type={PAYOUT_REVIEW_STATES.APPROVE}
          />
        ),
        
        classes: '!p-0 r-[5px]',
        size: 'sm'
      });
    }
  };

  const requestForPayout = (payout: Payout) => {
    if (payout) {
      updateModalStates({
        show: true,
        children: (
          <PayoutReview
            payout={payout}
            onProceed={() => {
              closeModal();
              queryClient.invalidateQueries({
                queryKey: ['getPayouts']
              });
            }}
            onCancel={closeModal}
            title="Request for payout"
            type={PAYOUT_REVIEW_STATES.REQUEST}
          />
        ),
        
        classes: '!p-0 r-[5px]',
        size: 'lg'
      });
    }
  };

  const markAsPaid = (payout: Payout) => {
    if (payout) {
      updateModalStates({
        show: true,
        children: (
          <MarkAsPaidModal
            payout={payout}
            onProceed={() => {
              closeModal();
              queryClient.invalidateQueries({
                queryKey: ['getPayouts']
              });
            }}
            onCancel={closeModal}
            title="Mark as paid"
            type={PAYOUT_REVIEW_STATES.PAID}
            titleIcon={undefined}
          />
        ),
        
        classes: '!p-0 r-[5px]',
        size: 'lg'
      });
    }
  };

  const getTableRowActions = (payout: Payout) => {
    const actions = [];

    if (user.isOwner() && !payout.isPaid && payout.isReviewed) {
      actions.push({
        label: `${(payout?.payoutType === 'COMMISSION' && user.isBroker()) ? 'Receive' : 'Confirm'} Payout`,
        callback: () => superApprovePayout(payout)
      });
    }

    if (user.isAdministrator() && !payout.isPaid && !payout.isReviewed) {
      actions.push({
        label: 'Request Payout',
        callback: () => requestForPayout(payout)
      });
    }

    if (user.isAdministrator() && !payout.isPaid && payout.isReviewed && payout.isApproved) {
      actions.push({
        label: 'Mark as paid',
        callback: () => markAsPaid(payout)
      });
    }

    return actions;
  };

  const fetchPayouts = async (paidValue?: string) => {

    try {
      const response: any = await getAllInsurerPayouts(currentPage, showPerPage, insurer, broker, fromDate, toDate, paidValue === 'null' ? undefined : isPaid, isApproved, reviewStage, searchString)

      if (response) {
        
        return response;
        
      }
    } catch (error: any) {
      showToast.error(error?.description ?? error.message);
    } finally {
     
    }
  };

  const handlePageChange = (page: number) => {

    const updatedParams = new URLSearchParams(searchParams);
    updatedParams.set("page", page.toString());
    setSearchParams(updatedParams);
  };

  const getFilter = () => {
    return (
      <div className="flex flex-col items-center gap-2 max-w-[10rem]">
        <TableFilter
          openFilter={openFilter}
          onFilterChange={(filterState: boolean) => setOpenFilter(filterState)}
          selectedFilters={tableFilters}
          content={
            <TableFilterForm
              filters={tableFilters}
              getValues={(filters: ITableFilterItem[]) => {
                setTableFilters(filters);
                setOpenFilter(false);

                updateQueriesList(filters, queriesList)
                setSearchParams(queriesList)

              }}
              onResetFilters={() => setTableFilters(getDefaultFilters())}
            />
          }
        />
      </div>
    )
  }

  useEffect(() => {
    updateQueriesList(tableFilters, queriesList)
    setSearchParams(queriesList)
  }, [tableFilters])

  useEffect(() => {
    onPayoutsUpdated?.(payouts);
  }, [payouts]);

  useEffect(() => {
    setTableFilters(getDefaultFilters());
  }, [insurersList, brokersList]);


  return (
    <Page title={`${capitalizeSentence(page)}`}>
      <div className="bg-white dark:bg-darkBg w-full p-5 text-sm min-h-full rounded-lg">
        <div className="flex items-center justify-between">

          {showTitleSection && (
            <div className="">
              <div className="inline-flex items-center gap-2 mb-2">
                <div className="h-8 w-2 bg-gradient-to-b from-yellow-500 to-yellow-700 rounded"></div>
                <h2 className="font-semibold text-3xl text-gray-900 dark:text-darkBg-mainText">{capitalizeSentence(page)}s</h2>
              </div>

              <p className="text-xs sm:text-sm text-slate-400 dark:text-darkBg-subText">
                View, manage and track premium payments to insurers, including outstanding and settled amounts.
              </p>
            </div>
          )}

          {/* Filters & Actions */}
          <div className="flex items-center space-x-3">
            {getFilter()}
          </div>
        </div>

        <div className="my-6">
          <PayoutsOverviewPanel data={payoutMetrics} isLoading={isLoadingPayoutMetrics} onCardClick={(cardTitle: boolean | null) => handlePayoutCardClick(cardTitle)} />
        </div>


        <div className="overflow-x-auto  mt-4">
          <GTable
            columns={columns}
            data={insurerPayouts || []}
            showPagination
            searchPlaceholder="Search by reference"
            tableType={'payouts'}
            loading={isLoading}
            showPerPage={showPerPage}
            paginationData={pagination}
            onPageChange={handlePageChange}
            selectedFilters={tableFilters}
            onShowPerPageChange={(page: number) => setShowPerPage(page)}
            onRemoveFilter={(filter: ITableFilterItem, index: number) => {
              onRemoveFilter(filter, index, tableFilters, setTableFilters, setSearchParams, queriesList)
            }}
            showSearch={true}
            onSearch={(value: string) => setSearchString(value)}
            // onRowClick={(payout: Payout) => {
            //   navigate(`${getDashboardBase()}/payouts/${payout.id}?type=${payout.payoutType?.toLowerCase()}`)
            // }}
            classes={'h-fit'}
          />
        </div>

        <ModalComponent />
      </div>
    </Page>
  );
};

export default PayoutsView;
